import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  DatePicker,
  Empty,
  Popover,
  InputNumber,
  Select,
  Radio,
  Checkbox,
  Popconfirm,
  notification,
} from 'antd';
import { ScheduleOutlined, DeleteOutlined } from '@ant-design/icons';
import directory from '@tutorbot/directory-api';
import { LocalizationContext } from '../../hoc/Localization';
import { LocationContext } from '../../hoc/Location';
import { UserContext } from '../../hoc/User';
import { getCourses } from '../../utils/directory';
import CardList from '../../components/CardList';
import Button from '../../components/Button';
import Loading from '../../components/Loading';
import Link from '../../components/Link';
import Noschedule from '../../components/NoSchedule';
import api from '../../utils/api';
import styles from './styles.less';
import Input from '../../components/Input';

const MyCourses = ({ mode = 'courses' }) => {
  const { t } = React.useContext(LocalizationContext);
  const { user } = React.useContext(UserContext);
  const { locations } = React.useContext(LocationContext);
  const [courses, setCourses] = React.useState(null);
  const [focused, setFocused] = React.useState(null);
  const [weekDays, setWeekDays] = React.useState([]);
  const [dayTimes, setDayTimes] = React.useState([]);
  const [currentSchedules, setCurrentSchedules] = React.useState([]);

  const countries = locations
    .filter(location => location.default)
    .map(location => location.country);
  React.useEffect(() => {
    (async () => {
      if (user && Object.keys(user).length) {
        const school = user.schools.filter(s => !!s).map(s => s.shortId);
        setCourses(
          await getCourses({
            filters: {
              school,
              countries,
            },
            verified: 'all',
            limit: 999,
          }).then(res => res.data),
        );

        setWeekDays(await directory().weekDays.all());
        setDayTimes(await directory().dayTimes.all());
      } else {
        setCourses([]);
      }
    })();
  }, [user]);

  const createCourseProps = {
    route: 'createCourse',
  };

  if (user && user.schools.length === 1) {
    createCourseProps.school = user.schools[0].shortId;
  }

  const fields = {
    start_date: 'datepicker',
    end_date: 'datepicker',
    duration_type: 'duration_type',
    weekdays: 'choose',
    daytimes: 'choose',
    available_seats: 'select',
  };

  const saveSchedule = async (courseIndex, scheduleIndex) => {
    const schedule = { ...courses[courseIndex].schedules[scheduleIndex] };
    schedule.course = courses[courseIndex].id;
    schedule.weekdays = schedule.weekdays.map(o => o.id);
    schedule.daytimes = schedule.daytimes.map(o => o.id);
    if (schedule.custom_weekdays === true) {
      schedule.custom_weekdays = false;
    }

    const record = await api.put(`courses/${schedule.course}/schedule`, {
      schedule,
      course: courses[courseIndex],
    });
    courses[courseIndex].schedules[scheduleIndex]._id = record._id;
    courses[courseIndex].schedules[scheduleIndex].id = record.id;
    setCourses(courses);
  };

  const appendSchedule = courseIndex => {
    const lengthSchedules = courses[courseIndex].schedules.length;
    if (!lengthSchedules) {
      courses[courseIndex].schedules.push({
        course: courses[courseIndex].id,
        duration_type: 'week',
        weekdays: [],
        daytimes: [],
      });
    } else if (t && courses[courseIndex].schedules[lengthSchedules - 1].start_date) {
      courses[courseIndex].schedules.push({
        course: courses[courseIndex].id,
        duration_type: 'week',
        weekdays: [],
        daytimes: [],
      });
    }

    setCurrentSchedules(courses[courseIndex].schedules);
    setCourses(courses);
  };

  const deleteSchedule = (courseIndex, scheduleIndex) => {
    if (courses[courseIndex].schedules[scheduleIndex].start_date) {
      courses[courseIndex].schedules[scheduleIndex].deleted = true;
      setCurrentSchedules(courses[courseIndex].schedules);
      setCourses(courses);
      saveSchedule(courseIndex, scheduleIndex);
    } else if (!courses[courseIndex].schedules[scheduleIndex].start_date) {
      courses[courseIndex].schedules[scheduleIndex].deleted = true;
      setCurrentSchedules(courses[courseIndex].schedules);
      setCourses(courses);
      // saveSchedule(courseIndex, scheduleIndex);
    }
  };

  const updateSchedule = (courseIndex, scheduleIndex, field, value) => {
    courses[courseIndex].schedules[scheduleIndex][field] = value;
    setCourses(courses);

    const keys = Object.keys(fields);
    const index = keys.findIndex(f => f === field);
    if (fields[field] !== 'choose' && index !== -1 && index < keys.length - 2) {
      const next = keys[index + 1];
      setCurrentSchedules(courses[courseIndex].schedules);
      setFocused(`${next}${courseIndex}${scheduleIndex}`);
    }

    if (courses[courseIndex].schedules[scheduleIndex].start_date) {
      saveSchedule(courseIndex, scheduleIndex);
      setCurrentSchedules(courses[courseIndex].schedules);
    } else {
      notification.warning({
        message: (
          <div>
            <h3>{t('start.date.required.text')}</h3>
          </div>
        ),
      });
    }
  };
  React.useEffect(() => {
    setCurrentSchedules(courses);
  }, [currentSchedules]);

  const input = (courseIndex, scheduleIndex, field) => {
    const schedule = courses[courseIndex].schedules[scheduleIndex];
    const type = fields[field];

    let value;

    switch (type) {
      case 'datepicker':
        value = !!schedule[field] && moment(schedule[field]).format('LL');
        break;
      case 'duration_type':
        value =
          !!schedule.duration_weeks &&
          !!schedule.duration_type &&
          `${schedule.duration_weeks} ${schedule.duration_type}`;
        break;
      case 'choose':
        if (field === 'weekdays' && schedule.custom_weekdays) {
          value = schedule.custom_weekdays;
          break;
        }

        value =
          !!schedule[field].length &&
          schedule[field]
            .map(o =>
              (field === 'weekdays'
                ? t(`course.schedule.days.${o.title.toLowerCase()}`).substr(0, 3)
                : o.title),
            )
            .join(' ');
        break;
      default:
        value = schedule[field];
        break;
    }

    const button = (
      <button
        type="submit"
        onClick={() => setFocused(`${field}${courseIndex}${scheduleIndex}`)}
        onFocus={() => setFocused(`${field}${courseIndex}${scheduleIndex}`)}
      >
        {value || '-'}
      </button>
    );

    if (type === 'datepicker') {
      return (
        <>
          {button}
          <DatePicker
            defaultValue={schedule[field] ? moment(schedule[field]) : null}
            open={focused === `${field}${courseIndex}${scheduleIndex}`}
            onOpenChange={() => setFocused(null)}
            onChange={v =>
              updateSchedule(courseIndex, scheduleIndex, field, v ? v.format('YYYY-MM-DD') : null)}
            className={styles.datePicker}
          />
        </>
      );
    }
    if (type === 'duration_type' || type === 'choose') {
      let content;

      if (field === 'duration_type') {
        content = (
          <div className={styles.flex}>
            <InputNumber
              min={1}
              max={99}
              defaultValue={schedule.duration_weeks}
              onChange={v => updateSchedule(courseIndex, scheduleIndex, 'duration_weeks', v)}
              style={{ marginRight: '10px' }}
            />

            <Select
              defaultValue={schedule[field]}
              onChange={v => updateSchedule(courseIndex, scheduleIndex, field, v)}
              style={{ width: '85px' }}
            >
              <Select.Option value="week">Week</Select.Option>
              <Select.Option value="day">Day</Select.Option>
            </Select>
          </div>
        );
      } else if (field === 'weekdays') {
        content = (
          <>
            <Radio.Group
              buttonStyle="solid"
              defaultValue={!!schedule.custom_weekdays}
              onChange={v =>
                updateSchedule(courseIndex, scheduleIndex, 'custom_weekdays', v.target.value)}
            >
              <Radio.Button value={false}>WeekDays</Radio.Button>
              <Radio.Button value>Custom</Radio.Button>
            </Radio.Group>

            <div className={styles.weekdays}>
              {schedule.custom_weekdays ? (
                <Input
                  version="default"
                  size="default"
                  onChange={v =>
                    updateSchedule(courseIndex, scheduleIndex, 'custom_weekdays', v.target.value)}
                />
              ) : (
                <Select
                  defaultValue={schedule.weekdays.map(o => o.id)}
                  onChange={v =>
                    updateSchedule(
                      courseIndex,
                      scheduleIndex,
                      field,
                      weekDays.filter(o => v.includes(o.id)),
                    )}
                  style={{ width: '100%' }}
                  mode="multiple"
                >
                  {weekDays.map(weekDay => (
                    <Select.Option key={weekDay.id} value={weekDay.id}>
                      {weekDay.title}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </div>
          </>
        );
      } else if (field === 'daytimes') {
        const dayTimesOptions = dayTimes.map(daytime => ({
          label: daytime.title,
          value: daytime.id,
        }));

        content = (
          <Checkbox.Group
            defaultValue={schedule.daytimes.map(o => o.id)}
            onChange={v =>
              updateSchedule(
                courseIndex,
                scheduleIndex,
                field,
                dayTimes.filter(o => v.includes(o.id)),
              )}
            options={dayTimesOptions}
          />
        );
      }

      return (
        <Popover
          placement="bottom"
          content={content}
          visible={focused === `${field}${courseIndex}${scheduleIndex}`}
          onVisibleChange={() => setFocused(null)}
        >
          {button}
        </Popover>
      );
    }

    if (type === 'select') {
      return (
        <>
          {button}

          <Select
            defaultValue={schedule[field]}
            onChange={v => {
              updateSchedule(courseIndex, scheduleIndex, field, v);
              setFocused(null);
            }}
            onDropdownVisibleChange={() => setFocused(null)}
            open={focused === `${field}${courseIndex}${scheduleIndex}`}
            // style={{ width: '100%' }}
          >
            {Array.from({ length: 20 }, (_x, i) => i + 1).map(i => (
              <Select.Option value={i}>{i}</Select.Option>
            ))}
          </Select>
        </>
      );
    }

    return button;
  };

  return (
    <div className="container">
      <div className={styles.courses}>
        <h2>
          {t('menu.myCourses')}

          {!!courses &&
            !!courses.length &&
            (mode === 'courses' ? (
              <Link route="mySchedules">
                <Button size="default" type="primary">
                  <ScheduleOutlined />
                  Edit schedules
                </Button>
              </Link>
            ) : (
              <Link route="myCourses">
                <Button size="default" type="primary">
                  Back to courses
                </Button>
              </Link>
            ))}
        </h2>

        {!courses ? (
          <Loading />
        ) : (
          <>
            {/* eslint-disable-next-line no-nested-ternary */}
            {courses.length ? (
              mode === 'courses' ? (
                <CardList data={courses} noButton />
              ) : (
                <div>
                  {courses.map((course, courseIndex) => (
                    <div className={styles.course} key={course.id || Date.now()}>
                      <div className={styles.head}>
                        <img src={course.image} alt={course.title} />
                        <p>
                          <Link
                            route="course"
                            school={course.school.link}
                            course={course.link}
                            id={course.shortId}
                          >
                            {course.title}
                          </Link>
                        </p>
                      </div>

                      <div className={styles.schedules}>
                        {course.schedules && course.schedules[0].start_date ? null : <Noschedule />}
                        <table>
                          <tbody>
                            <tr>
                              <th width="15%">Start Date</th>
                              <th width="15%">End Date</th>
                              <th width="12%">Duration</th>
                              <th width="20%">Weekdays</th>
                              <th width="20%">Daytime</th>
                              <th width="12%">Seats</th>
                              <th width="12%">Delete</th>
                            </tr>

                            {course.schedules.map(
                              (schedule, scheduleIndex) =>
                                !schedule.deleted && (
                                  <tr className={styles.editable} key={schedule.id}>
                                    {Object.keys(fields).map(field => (
                                      <td key={field} className={`${field}_field`}>
                                        {input(courseIndex, scheduleIndex, field)}
                                      </td>
                                    ))}

                                    <td>
                                      <Popconfirm
                                        title={`${t('course.schedule.delete.message')}`}
                                        okText={`${t('addschool.confirm.yes')}`}
                                        cancelText={`${t('addschool.confirm.no')}`}
                                        onConfirm={() => deleteSchedule(courseIndex, scheduleIndex)}
                                      >
                                        <button type="submit">
                                          <DeleteOutlined />
                                        </button>
                                      </Popconfirm>
                                    </td>
                                  </tr>
                                ),
                            )}

                            <tr>
                              <td className={styles.addSchedule} colSpan={7}>
                                <Button
                                  size="large"
                                  type="primary"
                                  onClick={() => appendSchedule(courseIndex)}
                                >
                                  +
                                </Button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <Empty description={<span>No courses</span>}>
                <Link {...createCourseProps}>
                  <Button type="primary">{t('menu.course.create')}</Button>
                </Link>
              </Empty>
            )}
          </>
        )}
      </div>
    </div>
  );
};

MyCourses.propTypes = {
  mode: PropTypes.string,
};

MyCourses.defaultProps = {
  mode: 'courses',
};

MyCourses.getInitialProps = ({ asPath }) => {
  const mode = asPath.indexOf('schedules') !== -1 ? 'schedules' : 'courses';
  return { mode };
};

export default MyCourses;
